import {Center, Spinner} from 'platform/foundation';

import {useEffect} from 'react';
import {useParams} from 'react-router-dom';

import {useFindMatchQuery, useGetVehicleListQuery} from '@dms/api/sales';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

export const ExternalVehicleRedirect = () => {
  const navigate = useNavigate();
  const params = useParams<{externalVehicleId: string; '*': string}>();
  const externalVehicleId = params.externalVehicleId;
  const additionalPathSuffix = params['*'] ? `/${params['*']}` : '';

  // First query: using externalVehicleId as publicId
  const publicIdQuery = useGetVehicleListQuery(
    {
      publicId: externalVehicleId,
    },
    {
      skip: !externalVehicleId,
    }
  );

  // Second query: using externalVehicleId as externalId
  const externalIdQuery = useGetVehicleListQuery(
    {
      externalId: externalVehicleId,
    },
    {
      skip: !externalVehicleId,
    }
  );

  // Third query (highest priority): find match using otomoto platform
  const findMatchQuery = useFindMatchQuery(
    {
      platformCode: 'otomoto',
      externalId: externalVehicleId,
    },
    {
      skip: !externalVehicleId,
    }
  );

  const isLoading =
    findMatchQuery.isLoading || publicIdQuery.isLoading || externalIdQuery.isLoading;
  const allQueriesComplete =
    !findMatchQuery.isFetching && !publicIdQuery.isFetching && !externalIdQuery.isFetching;

  useEffect(() => {
    // Wait for all queries to complete
    if (!allQueriesComplete) {
      return;
    }

    // Check if any query returned at least one vehicle
    const otomotoIdVehicles = findMatchQuery.data || [];
    const publicIdVehicles = publicIdQuery.data || [];
    const externalIdVehicles = externalIdQuery.data || [];

    // Find the first vehicle from either query
    const foundVehicleId =
      otomotoIdVehicles[0]?.vehicleId || publicIdVehicles[0]?.id || externalIdVehicles[0]?.id;

    if (foundVehicleId) {
      // Redirect to vehicle detail page with additional path suffix preserved
      const finalPath = additionalPathSuffix
        ? `${vehiclesRoutes.vehicleOverview}/${foundVehicleId}${additionalPathSuffix}`
        : composePath(vehiclesRoutes.detail, {params: {id: foundVehicleId}});
      navigate(finalPath, {
        replace: true,
      });
    } else {
      // No vehicle found, redirect to home
      const finalPath = vehiclesRoutes.home;
      navigate(finalPath, {replace: true});
    }
  }, [
    allQueriesComplete,
    findMatchQuery.data,
    publicIdQuery.data,
    externalIdQuery.data,
    navigate,
    additionalPathSuffix,
  ]);

  // Show loading state while queries are running
  if (isLoading || !allQueriesComplete) {
    return (
      <Center height="100vh">
        <Spinner />
      </Center>
    );
  }

  // This should not be reached as useEffect handles redirection
  return null;
};
